const NPI_BASE = 'https://npiregistry.cms.hhs.gov/api';

function toTitleCase(str) {
  return (str || '').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
}

/**
 * Searches individual providers via our backend proxy → NPPES registry.
 * NPPES does not set CORS headers so we cannot call it directly from the browser.
 */
export async function searchDoctors(firstName, lastName, state = '') {
  if (!lastName.trim()) return [];

  const params = new URLSearchParams({ last_name: lastName.trim(), limit: '200' });
  if (firstName.trim()) params.set('first_name', firstName.trim());
  if (state) params.set('state', state);

  const res = await fetch(`/api/doctor-search?${params}`);
  if (!res.ok) return [];
  return res.json();
}

/**
 * Fetches pharmacies near a given ZIP code (or state fallback) from the NPI Registry.
 * Returns location-specific results (e.g. "Walgreens #5832 at 123 Main St").
 */
export async function searchNearbyPharmacies(zip, state) {
  const params = new URLSearchParams({
    version: '2.1',
    enumeration_type: 'NPI-2',
    taxonomy_description: 'pharmacy',
    limit: 25,
  });

  if (zip)        params.set('postal_code', zip);
  else if (state) params.set('state', state);
  else            return [];

  const res = await fetch(`${NPI_BASE}/?${params}`);
  if (!res.ok) return [];
  const data = await res.json();

  return (data.results || []).map(r => {
    const addr = (r.addresses || []).find(a => a.address_purpose === 'LOCATION')
      || r.addresses?.[0]
      || {};
    return {
      name: toTitleCase(r.basic?.organization_name || ''),
      address: toTitleCase(addr.address_1 || ''),
      city: toTitleCase(addr.city || ''),
      state: addr.state || '',
    };
  }).filter(p => p.name);
}

const NLM_NPI_ORG_BASE = 'https://clinicaltables.nlm.nih.gov/api/npi_org/v3/search';

export async function searchPharmaciesByName(name, zip, state) {
  const term = name?.trim();
  if (!term) return [];

  // Build URL manually — URLSearchParams encodes ':' as %3A and ',' as %2C,
  // which the NLM Lucene query parser rejects with a 400.
  // Use 3-digit ZIP prefix (metro area) rather than state — state returns results from across
  // the entire state, many of which are out-of-network for the user's local Medicare plan area.
  const geoFilter = zip      ? `&q=addr_practice.zip:${zip.slice(0, 3)}*`
                  : state    ? `&q=addr_practice.state:"${state}"`
                  : '';
  const url = `${NLM_NPI_ORG_BASE}?terms=${encodeURIComponent(term)}&maxList=50&df=NPI,name.full,addr_practice.city,addr_practice.state,addr_practice.zip${geoFilter}`;

  const res = await fetch(url);
  if (!res.ok) return [];
  const data = await res.json();
  const [, , , rows] = data;
  if (!rows) return [];

  // Normalize hyphens/spaces so "walmart" matches "WAL-MART"
  const termNorm = term.toLowerCase().replace(/[-\s]+/g, '');

  return rows
    .map(([npi, orgName, city, rowState, rowZip]) => ({
      npi:     npi || '',
      name:    toTitleCase(orgName || ''),
      city:    toTitleCase(city || ''),
      state:   rowState || '',
      zip:     (rowZip || '').slice(0, 5),
      address: '',
    }))
    .filter(p => {
      if (!p.name || !p.npi) return false;
      const nameNorm = p.name.toLowerCase().replace(/[-\s]+/g, '');
      return nameNorm.includes(termNorm);
    })
    .slice(0, 20);
}
